<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useToggle } from '@vueuse/core';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { buildDraftAIAssistOptions } from 'dashboard/helper/aiAssistOptions';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import AIAssistanceModal from 'dashboard/components/widgets/AIAssistanceModal.vue';

const props = defineProps({
  message: { type: String, default: '' },
});

const emit = defineEmits(['replaceText', 'update:open', 'update:modalOpen']);

const { t } = useI18n();

const [showDropdown, toggleDropdown] = useToggle();
const selectedOption = ref('');
const showModal = ref(false);

// Every option rewrites text that is already in the editor, so there is nothing to act on while it
// is empty. An image-only message is treated as empty here for the same reason.
const hasText = computed(() => !!props.message?.replace(/<[^>]*>/g, '').trim());

// Only the options that build their prompt from the draft alone. "Translate", "Summarize" and
// "Reply suggestion" need a conversation, which does not exist yet while composing.
const menuItems = computed(() =>
  buildDraftAIAssistOptions(t).map(({ key, label, icon }) => ({
    label,
    value: key,
    action: 'aiAssist',
    icon,
  }))
);

const closeDropdown = () => toggleDropdown(false);

const closeModal = () => {
  showModal.value = false;
  selectedOption.value = '';
};

const onSelectOption = ({ value }) => {
  selectedOption.value = value;
  showModal.value = true;
  closeDropdown();
};

const applyText = message => {
  emit('replaceText', message);
  closeModal();
};

// The compose modal binds Enter/Escape on the document to send and to discard. Tell it to stand
// down while either our dropdown or our modal owns the keyboard.
watch(
  () => showDropdown.value || showModal.value,
  isOpen => emit('update:open', isOpen)
);

// Reported separately from the above: only the full-screen modal should make the compose panel
// recede. A small dropdown floating over it must leave the panel looking normal.
watch(showModal, isOpen => emit('update:modalOpen', isOpen));

// Escape closes the dropdown first; only once it is gone does compose see Escape as "discard".
useKeyboardEvents({
  Escape: {
    action: () => {
      if (showDropdown.value) closeDropdown();
    },
  },
});
</script>

<template>
  <div v-on-click-outside="closeDropdown" class="relative">
    <Button
      v-tooltip.top-end="$t('INTEGRATION_SETTINGS.OPEN_AI.AI_ASSIST')"
      icon="i-ph-magic-wand"
      color="slate"
      size="sm"
      class="!w-10"
      :disabled="!hasText"
      @click="toggleDropdown()"
    />
    <DropdownMenu
      v-if="showDropdown"
      :menu-items="menuItems"
      class="bottom-full mb-2 ltr:left-0 rtl:right-0 w-56"
      @action="onSelectOption"
    />
    <!-- Teleported out of the compose panel: the panel dims itself while this is open, and an
         opacity on an ancestor would fade the dialog along with it.
         The target must stay inside the `dir` context — woot-modal places its close button with
         `ltr:right-2`, which silently falls back to the left edge without a `[dir]` ancestor.
         Note there are two #app nodes (the Rails mount point and App.vue's root); only the inner
         one carries `dir`, hence the attribute selector rather than plain `#app` or `body`.
         `v-if` on the Teleport itself (not just on the modal) so the teleported node cannot
         outlive this component — the compose form can be discarded while the dialog is open. -->
    <Teleport v-if="showModal" to="#app[dir]">
      <woot-modal v-model:show="showModal" @close="closeModal">
        <AIAssistanceModal
          v-if="selectedOption"
          :ai-option="selectedOption"
          :content="message"
          @apply-text="applyText"
          @close="closeModal"
        />
      </woot-modal>
    </Teleport>
  </div>
</template>
